import * as PIXI from 'pixi.js';
import uniq from 'lodash/uniq';
import type { HighlightLine } from '../core/text/HighlightLine';
import type { HighlightSpan } from '../core/text/HighlightSpan';
import type { TextWord } from '../core/text/TextWord';
import type { TextLine } from '../core/text/TextLine';
import { ParentMixin } from '../core/parentMixin';
import { UpdateFlowMixin } from '../core/updateFlowMixin';
import type { LoadSettings } from '../core/serializableMixin';
import { SerializableMixin } from '../core/serializableMixin';
import {
    BooleanAttribute,
    getAttributesToSerialize,
    StringAttribute
} from '../core/attributes';
import { Config } from '../video-data/config';
import { BoxGraphic } from '../core/slide/graphicsStyle/BoxGraphic';
import { CircleGraphic } from '../core/slide/graphicsStyle/CircleGraphic';
import { BoxBorderGraphic } from '../core/slide/graphicsStyle/BoxBorderGraphic';

import { assertInEnum } from '../validators';
import type { Container } from '../core/asset/Container';
import { DynamicStylingCreationLevel, AllGraphicClassTypes, CreationType } from '../enums';
import { DynamicAnimationConfiguration } from './DynamicAnimationConfiguration';
import { DynamicBoxConfigurator } from './DynamicBoxConfigurator';
import { DynamicCircleConfigurator } from './DynamicCircleConfigurator';
import { DynamicBoxBorderConfigurator } from './DynamicBoxBorderConfigurator';

export class DynamicGraphicsConfigurator extends UpdateFlowMixin(ParentMixin(SerializableMixin(PIXI.utils.EventEmitter))) {
    override _name = 'dynamicGraphicsConfigurator';
    override _classType = 'DynamicGraphicsConfigurator';

    @StringAttribute({ choices: Config.ALL_GRAPHICS })
    public graphic: typeof Config.ALL_GRAPHICS[number] = Config.BOX_GRAPHIC;
    @BooleanAttribute({ serialize: false, animatable: false })
    public enabled = true;
    @BooleanAttribute({ animatable: false })
    public asMask = false;
    @StringAttribute({ choices: Object.values(DynamicStylingCreationLevel) })
    public level = DynamicStylingCreationLevel.Object;
    @StringAttribute({ choices: Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL_OPTIONS })
    public alignGraphicX = Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.LEFT;
    @StringAttribute({ choices: Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL_OPTIONS })
    public alignGraphicY = Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.TOP;

    animation = [];
    shape: DynamicBoxConfigurator | DynamicCircleConfigurator | DynamicBoxBorderConfigurator = new DynamicBoxConfigurator({ parent: this, updateParentOnUpdate: true });
    _maskedContainers: Container[] = [];
    _graphics = [];
    #destroyed = false;

    get destroyed() {
        return this.#destroyed;
    }

    // Cache data about the containers so that multiple dynamic
    // graphics don't influence each other
    #containers = [];
    #containerProperties = [];
    #containerBounds = [];

    /**
     * Position line-level dynamic graphics
     * starting from last element instead of first.
     */
    _useLastElement = false;

    // eslint-disable-next-line no-useless-constructor
    constructor(settings) {
        super(settings);
    }

    /**
     * Returns the creation containers for the given level used for dynamic styling. Currently there are 3 different
     * levels: object, line and word. It is possible for an asset to only support one level. In that case, an empty
     * array will be returned for the unsupported level.
     * @param type - animation or graphic
     */
    getCreationContainersAtLevelForType(value: DynamicStylingCreationLevel, type = CreationType.Animation): any[] {
        assertInEnum(type, CreationType);
        let result = [];
        switch (value) {
            case DynamicStylingCreationLevel.Object:
            case DynamicStylingCreationLevel.Line:
            case DynamicStylingCreationLevel.Word:
            case DynamicStylingCreationLevel.Highlight:
                result = this._graphics.filter(graphic => !graphic.destroyed);
                break;
            default:
                throw new Error(`Unsupported container of type ${value}`);
        }
        return result;
    }

    /**
     * @returns A list of `classType` of the graphic shapes that this configurator can create.
     */
    get supportedShapeClassTypes(): string[] {
        return AllGraphicClassTypes;
    }

    _createShape(graphic) {
        this.graphic = graphic;
        switch (graphic) {
            case Config.BOX_GRAPHIC:
                this.shape = new DynamicBoxConfigurator({ parent: this, updateParentOnUpdate: true });
                break;
            case Config.CIRCLE_GRAPHIC:
                this.shape = new DynamicCircleConfigurator({ parent: this, updateParentOnUpdate: true });
                break;
            case Config.BOX_BORDER_GRAPHIC:
                this.shape = new DynamicBoxBorderConfigurator({ parent: this, updateParentOnUpdate: true });
                break;
            default:
                throw Error(`Unsupported graphic type: "${graphic}"`);
        }
    }

    override _checkAndFixProperties() {
        super._checkAndFixProperties();
        this._checkAndFixShape();
    }

    _checkAndFixShape() {
        if (this.shape?.graphicType !== this.graphic) {
            this._createShape(this.graphic);
        }
    }

    /**
     * Extract the necessary properties from our creation containers to create the graphics.
     * @param {object[]} containers - list of containers, which will hold the new the graphics
     * @returns {object[]} - list of properties for each graphic controlled by this configurator
     */
    _getGraphicProperties(containers) {
        const shapeProperties = this.shape.save(true, false, false);
        switch (this.level) {
            case DynamicStylingCreationLevel.Object: return this._objectLevelProperties(shapeProperties, containers);
            case DynamicStylingCreationLevel.Line: return this._lineLevelProperties(shapeProperties, containers);
            case DynamicStylingCreationLevel.Word: return this._wordLevelProperties(shapeProperties, containers);
            case DynamicStylingCreationLevel.Highlight: return this._highlightLevelProperties(shapeProperties, containers);
        }
    }

    _objectLevelProperties(properties, containers) {
        let inferredProperties = this._getGlobalInferredProperties(properties, containers);
        inferredProperties = this._getLocalInferredProperties(inferredProperties, containers[0]);
        this._getZigZagInferredProperties(inferredProperties);
        return [inferredProperties];
    }

    _buildObjectLevelGraphics(properties, containers) {
        this._clearOldGraphics(properties.length);
        const pixiGraphic = this._getOrCreateGraphic(0, containers[0]);
        this._loadGraphicProperties(pixiGraphic, properties[0]);
        if (this.asMask) {
            this._setMaskOnContainer(pixiGraphic, containers[0]);
        }
        const graphicPosition = this._getGraphicPosition(pixiGraphic, containers[0], this.#containerBounds[0]);
        pixiGraphic.x = graphicPosition.x;
        pixiGraphic.y = graphicPosition.y;
    }

    /**
     * Calculates a delta to add to the `positionY` of a graphic (any graphic
     * with a `boxHeight` setting) based on the different heights of lines.
     *
     * When a `boxHeight` of 'maxHeight' is used, the spacing between the boxes
     * must be consistent. Normally the box is drawn from y=0 (relative to the
     * text line). Since lines have different heights (ascenders, descenders and
     * some punctuation affects the line height), drawing from y=0 leads to
     * unevenly spaced boxes.
     *
     * This delta nudges the box relative the the ascent of the highest line,
     * which spaces the boxes evenly.
     *
     * @returns An array of ascent deltas to nudge positionY, or an array of 0
     * deltas if nudging is inappropriate (e.g. custom line height).
     */
    _calculateAscentDeltaY({ boxHeight }: { boxHeight: number | string; }, lines) {
        if (boxHeight !== Config.INFERRED_MAX_HEIGHT && boxHeight !== Config.INFERRED_HEIGHT_GAPLESS) {
            return new Array(lines.length).fill(0);
        }
        const maxAscent = lines.reduce((acc, line) => Math.max(line.metrics.ascent, acc), 0);
        return lines.map(line => line.metrics.ascent - maxAscent);
    }

    _lineLevelProperties(properties, lines) {
        const result = [];
        const ascentDy = this._calculateAscentDeltaY(properties, lines);
        properties = this._getGlobalInferredProperties(properties, lines);
        const maxHeight = Math.max(...lines.map(line => line.getBounds().height));
        lines.forEach((line, index) => {
            const inferredProperties = this._getLocalInferredProperties(properties, line);
            if (inferredProperties.boxHeight === Config.INFERRED_HEIGHT_GAPLESS) {
                let boxHeight = maxHeight;
                if (line.hasVisibleText) {
                    // We will adjust the height to remove gaps around the line. We only remove gaps
                    // of lines which are shorter than the lines above/below.
                    const { topGap, bottomGap } = this._getLineGapHeight(lines, index, maxHeight, ascentDy);
                    boxHeight = maxHeight + topGap + bottomGap;
                    inferredProperties.positionY -= topGap;
                }
                inferredProperties.boxHeight = boxHeight;
            }
            inferredProperties.positionY += ascentDy[index];
            this._getZigZagInferredProperties(inferredProperties, index);
            result.push(inferredProperties);
        });
        return result;
    }

    _buildLineLevelGraphics(properties, lines: TextLine[]) {
        this._clearOldGraphics(lines.length);
        lines.forEach((line, index) => {
            if (!line.hasVisibleText) {
                return;
            }
            const pixiGraphic = this._getOrCreateGraphic(index, line.parent);
            this._loadGraphicProperties(pixiGraphic, properties[index]);
            if (this.asMask) {
                this._setMaskOnContainer(pixiGraphic, line);
            }
            const graphicPosition = this._getGraphicPosition(pixiGraphic, line, this.#containerBounds[index]);

            // When aligning our lines of text, we don't move the lines. Instead, we move the words. As a result, when
            // we parent our graphic to a line, it does not line up. If we want it to line up, we have to line it up
            // with the first word of the line, which we're doing here.
            pixiGraphic.y = line.y + graphicPosition.y;
            const words = line.textWords;
            const elementIndex = this._useLastElement ? words.length - 1 : 0;
            pixiGraphic.x = words[elementIndex].wordElementSprites[0].x + graphicPosition.x;
        });
    }

    _wordLevelProperties(properties, lines: TextWord[][]) {
        let words = lines.flat();
        properties = this._getGlobalInferredProperties(properties, words);
        words = words.filter(word => word.hasVisibleText);
        return words.map((word, index) => {
            const inferredProperties = this._getLocalInferredProperties(properties, word);
            this._getZigZagInferredProperties(inferredProperties, index);
            return inferredProperties;
        });
    }

    _buildWordLevelGraphics(properties, lines: TextWord[][]) {
        this._clearOldGraphics(lines.flat().length);
        let wordCounter = 0;
        lines.forEach(line => {
            line.forEach(word => {
                if (!word.hasVisibleText) {
                    return;
                }
                const pixiGraphic = this._getOrCreateGraphic(wordCounter, word.parent);
                this._loadGraphicProperties(pixiGraphic, properties[wordCounter]);
                if (this.asMask) {
                    this._setMaskOnContainer(pixiGraphic, word);
                }
                const graphicPosition = this._getGraphicPosition(pixiGraphic, word, this.#containerBounds[wordCounter]);

                const firstSprite = word.wordElementSprites[0];
                pixiGraphic.x = firstSprite.x + graphicPosition.x;
                pixiGraphic.y = firstSprite.y + graphicPosition.y;
                wordCounter++;
            });
        });
    }

    /**
     * Highlight level properties are a blend of line and span properties.
     * Horizontal coordinates/dimensions are taken from the spans.
     * Vertical coordinates/dimensions are taken from the lines.
     */
    _highlightLevelProperties(properties, spans: HighlightSpan[][]) {
        const mapLineToInferredProperties = new Map<HighlightLine, any>();
        const spansList = spans.flat();

        // Get the line properties first as we want the height and y offset info.
        const highlightLines = uniq(spansList.map(span => span.parent));
        this._lineLevelProperties(properties, highlightLines)
            .forEach((lineProperty, index) => mapLineToInferredProperties.set(highlightLines[index], lineProperty));

        // Now process the spans, merging in the height and y offset info from the highlight lines.
        properties = this._getGlobalInferredProperties(properties, spansList);

        const spanResults = spansList.filter(span => !span.isPlaceholder).map((span, index) => {
            const inferredProperties = this._getLocalInferredProperties(properties, span);
            const lineProperties = mapLineToInferredProperties.get(span.parent as HighlightLine);
            this._getZigZagInferredProperties(inferredProperties, index);
            // Merge the results of the line height and y offset into the span results.
            inferredProperties.boxHeight = lineProperties.boxHeight;
            inferredProperties.positionY = lineProperties.positionY;
            return inferredProperties;
        });

        return spanResults;
    }

    _buildHighlightLevelGraphics(properties, lines: HighlightSpan[][]) {
        this._clearOldGraphics(lines.flat().length);
        let spanCounter = 0;
        lines.forEach(line => {
            line.forEach(span => {
                if (span.isPlaceholder) {
                    return;
                }
                const pixiGraphic = this._getOrCreateGraphic(spanCounter, span.parent);
                this._loadGraphicProperties(pixiGraphic, properties[spanCounter]);
                if (this.asMask) {
                    this._setMaskOnContainer(pixiGraphic, span);
                }
                const graphicPosition = this._getGraphicPosition(pixiGraphic, span, this.#containerBounds[spanCounter]);

                // depending on readingDirection, left boundary can be first or last sprite
                const spriteLeftBoundary = Math.min(
                    span.wordElementSprites[0].x,
                    span.wordElementSprites[span.wordElementSprites.length - 1].x
                );

                pixiGraphic.x = span.x + spriteLeftBoundary + graphicPosition.x;
                pixiGraphic.y = graphicPosition.y;
                spanCounter++;
            });
        });
    }

    _getOrCreateGraphic(index: number, container) {
        let graphic: PIXI.Container;
        // Graphics can get destroyed outside of the configurator if its parent gets destroyed
        if (this._graphics[index] && !this._graphics[index].destroyed) {
            graphic = this._graphics[index];
        }
        else {
            graphic = this._createGraphic();
            this._graphics.push(graphic);
        }
        if (container instanceof PIXI.Container) {
            container.addChildAt(graphic, 0);
        }
        else {
            container._addInternalChildAt(graphic, 0);
        }
        return graphic;
    }

    _createGraphic() {
        let pixiGraphic;
        switch (this.graphic) {
            case Config.BOX_GRAPHIC: pixiGraphic = new BoxGraphic({}); break;
            case Config.CIRCLE_GRAPHIC: pixiGraphic = new CircleGraphic({}); break;
            case Config.BOX_BORDER_GRAPHIC: pixiGraphic = new BoxBorderGraphic({}); break;
            default: throw new Error(`Unrecognized graphic ${this.graphic}`);
        }

        return pixiGraphic;
    }

    _loadGraphicProperties(graphic, properties) {
        const initAutoUpdate = graphic.autoUpdate;
        graphic.autoUpdate = false;
        graphic.load(properties);
        graphic.autoUpdate = initAutoUpdate;
    }

    _getGlobalInferredProperties(properties, containers) {
        const inferredProperties: any = {};
        Object.keys(properties).forEach(property => {
            let value = properties[property];
            if (typeof value === 'string') {
                switch (value) {
                    case Config.INFERRED_MAX_WIDTH:
                        value = Math.max(...containers.map(container => container.getBounds().width));
                        break;
                    case Config.INFERRED_MAX_HEIGHT:
                        value = Math.max(...containers.map(container => container.getBounds().height));
                        break;
                    case Config.INFERRED_HEIGHT_GAPLESS:
                        if (this.level !== DynamicStylingCreationLevel.Line) {
                            // Gapless is not supported at this level.
                            // Default to max height
                            value = Math.max(...containers.map(container => container.getBounds().height));
                        }
                        break;
                }
            }
            inferredProperties[property] = value;
        });
        return inferredProperties;
    }

    /**
     * Gets the size of any gaps/overlaps to remove above and bellow the current line.
     * Note that an overlap corresponds to a gap smaller than 0.
     */
    _getLineGapHeight(lines, index, lineHeight, ascentDy) {
        const line = lines[index];
        const lineBounds = line.metrics.getBounds();
        const lineWidth = lineBounds.width;

        const totalPadding = this.shape.paddingBottom + this.shape.paddingTop;
        const getLineTop = (bounds, index) => bounds.y + ascentDy[index];
        const getLineBottom = (bounds, index) => bounds.y + lineHeight + totalPadding + ascentDy[index];
        let topGap = 0;
        let bottomGap = 0;

        // Find vertical gap/overlap with the previous line if it's wider or equal
        if (index > 0) {
            const previousTextLine = lines[index - 1];
            const previousBounds = previousTextLine.metrics.getBounds();
            const previousLineWidth = previousBounds.width;

            if (previousTextLine.hasVisibleText && previousLineWidth >= lineWidth) {
                topGap = getLineTop(lineBounds, index) - getLineBottom(previousBounds, index - 1);
            }
        }

        // Find vertical gap/overlap with the next line if it's wider
        if (index < lines.length - 1) {
            const nextTextLine = lines[index + 1];
            const nextBounds = nextTextLine.metrics.getBounds();
            const nextLineWidth = nextBounds.width;
            if (nextTextLine.hasVisibleText && nextLineWidth > lineWidth) {
                bottomGap = getLineTop(nextBounds, index + 1) - getLineBottom(lineBounds, index);
            }
        }

        return { topGap, bottomGap };
    }

    /**
     * Gets the height of the container. If the container contains text, the height is adjusted to the baseline by
     * subtracting the height of the descenders of the last line of text.
     * @returns The adjusted height.
     */
    _getDescentAdjustedHeight(container) {
        // We can't use our Container.getAssets({ deep: false }) method, as it ignores internal nodes.
        // Graphic objects are internal objects applied to internal object (e.g. TextLine).
        const _recursivelyGetTextLines = (containers, result) => {
            if (containers.length === 0) {
                return result;
            }

            for (const container of containers) {
                const lines = container.children.filter(child => child.classType === 'TextLine');
                if (lines.length > 0) {
                    result.push(...lines);
                }
                _recursivelyGetTextLines(container.children, result);
            }

            return result;
        };

        const _getTextLines = (container) => {
            if (container.classType === 'TextLine') {
                return [container];
            }
            return _recursivelyGetTextLines(container.children, []);
        };

        const lines = _getTextLines(container);
        if (lines.length === 0) {
            // The `descent` has no meaning in the case of no text lines. Better to hide the object with 0 height
            // than create strange configurations in the editor.
            return 0;
        }

        const lastLine = lines[lines.length - 1];
        const bounds = container.getBounds();

        // 'descent' is a negative number, therefore the addition.
        return bounds.height + lastLine.metrics.descent;
    }

    _getLocalInferredProperties(properties, container) {
        const bounds = container.getBounds();
        const inferredProperties: any = {};
        Object.keys(properties).forEach(property => {
            let value = properties[property];
            if (typeof value === 'string') {
                switch (value) {
                    case Config.INFERRED_WIDTH: value = bounds.width; break;
                    case Config.INFERRED_HEIGHT: value = bounds.height; break;
                    case Config.INFERRED_HEIGHT_WITHOUT_DESCENT: value = this._getDescentAdjustedHeight(container); break;
                }
            }
            inferredProperties[property] = value;
        });
        return inferredProperties;
    }

    _getZigZagInferredProperties(properties, index = 0) {
        if (properties.horizontalDirection === Config.ZIG_ZAG_LEFT_RIGHT_DIRECTION) {
            properties.horizontalDirection = (index % 2) ? Config.RIGHT_ANIMATION_DIRECTION : Config.LEFT_ANIMATION_DIRECTION;
        }
        if (properties.horizontalDirection === Config.ZIG_ZAG_RIGHT_LEFT_DIRECTION) {
            properties.horizontalDirection = (index % 2) ? Config.LEFT_ANIMATION_DIRECTION : Config.RIGHT_ANIMATION_DIRECTION;
        }
        if (properties.verticalDirection === Config.ZIG_ZAG_TOP_BOTTOM_DIRECTION) {
            properties.verticalDirection = (index % 2) ? Config.BOTTOM_ANIMATION_DIRECTION : Config.TOP_ANIMATION_DIRECTION;
        }
        if (properties.verticalDirection === Config.ZIG_ZAG_BOTTOM_TOP_DIRECTION) {
            properties.verticalDirection = (index % 2) ? Config.TOP_ANIMATION_DIRECTION : Config.BOTTOM_ANIMATION_DIRECTION;
        }
    }

    _getGraphicPosition(graphic, container, containerBounds) {
        const { width: containerWidth, height: containerHeight } = containerBounds;
        const { width: graphicWidth, height: graphicHeight } = graphic.getBounds();

        let x = 0;
        const paddingRight = graphic.paddingRight ?? 0;
        const paddingLeft = graphic.paddingLeft ?? 0;
        switch (this.alignGraphicX) {
            case Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.LEFT:
                x = -paddingLeft;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.CENTER:
                const widthWithoutPadding = graphicWidth - paddingLeft - paddingRight;
                x = (containerWidth - widthWithoutPadding) / 2 - paddingLeft;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.RIGHT:
                x = containerWidth - (graphicWidth - paddingRight);
                break;
        }

        let y = 0;
        const paddingTop = graphic.paddingTop ?? 0;
        const paddingBottom = graphic.paddingBottom ?? 0;
        switch (this.alignGraphicY) {
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.TOP:
                y = -paddingTop;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.CENTER:
                const heightWithoutPadding = graphicHeight - paddingTop - paddingBottom;
                y = (containerHeight - heightWithoutPadding) / 2 - paddingTop;
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.BOTTOM:
                y = containerHeight - (graphicHeight - paddingBottom);
                break;
            case Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.BASELINE:
                if (Config.isTextAssetStyle(this.parent)
                    && (this.level === DynamicStylingCreationLevel.Line || this.level === DynamicStylingCreationLevel.Word)) {
                    y = container.metrics.ascent - (graphicHeight - paddingBottom);
                }
                else if (Config.isTextAssetStyle(this.parent) && this.level === DynamicStylingCreationLevel.Highlight) {
                    y = container.parent.metrics.ascent - (graphicHeight - paddingBottom);
                }
                else {
                    y = containerHeight - (graphicHeight - paddingBottom);
                }
                break;
        }

        return { x, y };
    }

    /**
     * cleans up graphics which should no longer exist because the graphic type changed or
     * the graphic has been destroyed
     * @param numExistingGraphics
     */
    _clearOldGraphics(numExistingGraphics) {
        this.clear(numExistingGraphics);
        this._graphics = this._graphics.filter(graphic => {
            if (graphic.destroyed) {
                return false;
            }
            if (graphic._name !== this.graphic) {
                graphic.destroy();
                return false;
            }
            return true;
        });
    }

    /**
     * Clear or reset
     * @param startAt - start clearing graphics starting at this index
     */
    clear(startAt = 0) {
        const graphicsToClear = this._graphics.splice(startAt);
        graphicsToClear.forEach(graphic => {
            if (!graphic.destroyed) {
                graphic.destroy();
            }
        });
    }

    /**
     * Destroy
     */
    destroy() {
        this.#destroyed = true;
        this._removeMasksFromContainers();
        this.clear();
    }

    /**
     * Add animation
     */
    addAnimation(): DynamicAnimationConfiguration {
        const animationConfigurator = new DynamicAnimationConfiguration({ parent: this, updateParentOnUpdate: true });
        // Copy array so that React understands that the animations changed
        this.animation = [...this.animation, animationConfigurator];
        this.requiresUpdate = true;
        return animationConfigurator;
    }

    /**
     * Remove animation
     */
    removeAnimation(index: number) {
        this.animation.splice(index, 1);
        this.requiresUpdate = true;
    }

    /**
     * @inheritDoc
     */
    override get readonlyProperties() {
        return [];
    }

    /**
     * @inheritDoc
     */
    override get propertiesToSerialize() {
        return ['animation'].concat(getAttributesToSerialize(this));
    };

    override get editableProperties() {
        return super.editableProperties.concat(['graphic', 'shape']);
    }

    _animationCustomLoad(configData) {
        this.animation.splice(configData.length);
        configData.forEach((data, index) => {
            const animation = this.animation[index] ?? this.addAnimation();
            animation.load(data);
        });
    }

    _animationCustomSave() {
        return this.animation.map(animation => animation.save());
    }

    /**
     * This should be called for all dynamic styles before collectProperties.
     *
     * This removes the graphics from the asset hierarchy so they can be properly
     * removed or updated without influencing each other.
     * For example, we want to remove all color graphics first, otherwise once we do the
     * container calculations (i.e. getBounds) it will include the dynamic graphics as well
     */
    prepareForUpdate() {
        this._removeMasksFromContainers();
        this._unparentGraphics();
    }

    /**
     * This should be called for all dynamic styles before createOrUpdateGraphics.
     *
     * This runs the property calculations, using the containers data without any dynamic styles
     * attached to them. This ensures dynamic styles don't influence each other.
     */
    collectProperties() {
        this.#containers = this.parent?.getCreationContainersAtLevelForType(this.level, CreationType.Graphic) || [];
        if (this.#containers.length === 0) {
            this.#containerProperties = [];
            return;
        }
        this.#containerProperties = this._getGraphicProperties(this.#containers);
        if (this.level === DynamicStylingCreationLevel.Word) {
            // Word level code does not consider white space. Including it here would use whitespace bounds instead of
            // the actual word bounds.
            this.#containerBounds = this.#containers
                .flat()
                .filter(container => container.hasVisibleText)
                .map(container => container.getBounds());
        }
        else if (this.level === DynamicStylingCreationLevel.Highlight) {
            // Since highlight y coordinates are highlight *line* relative, the bounds need to be adjusted to fit the
            // highlight lines. This way the highlights on a line are of consistent height regardless of the ascenders
            // and descenders.
            this.#containerBounds = this.#containers
                .flat()
                .filter(container => !container.isPlaceholder) // Placeholders have no place here!
                .map(container => {
                    const bounds = container.getBounds();
                    const parentBounds = container.parent.getBounds();
                    bounds.y -= parentBounds.height - bounds.height;
                    bounds.height = parentBounds.height;
                    return bounds;
                });
        }
        else {
            this.#containerBounds = this.#containers.flat().map(container => container.getBounds());
        }
    }

    /**
     * After all dynamics styles have called prepareForUpdate and collectProperties, then they
     * can be built (or updated) by calling this method.
     */
    createOrUpdateGraphics() {
        if (this.destroyed) {
            return;
        }
        if (!this.enabled) {
            this.clear();
            return;
        }

        this._updateGraphics();
        this._updateAnimation();
    }

    _removeMasksFromContainers() {
        this._maskedContainers.forEach(container => {
            container.mask = null;
        });
        this._maskedContainers = [];
    }

    _unparentGraphics() {
        // We silently un-parent and parent graphics during property calculation, this way
        // getBounds calculations will exclude dynamic graphics which are being re-used
        this._graphics.forEach(graphic => {
            if (graphic.parent instanceof PIXI.Container) {
                graphic.parent.removeChild(graphic);
            }
            else {
                graphic.parent?._removeInternalChild(graphic);
            }
        });
    }

    _setMaskOnContainer(graphic, container) {
        container.mask = graphic;
        this._maskedContainers.push(container);
    }

    _updateGraphics() {
        if (this.#containers.length === 0) {
            this.clear();
            return;
        }

        // build graphic(s)
        switch (this.level) {
            case DynamicStylingCreationLevel.Object: this._buildObjectLevelGraphics(this.#containerProperties, this.#containers); break;
            case DynamicStylingCreationLevel.Line: this._buildLineLevelGraphics(this.#containerProperties, this.#containers); break;
            case DynamicStylingCreationLevel.Word: this._buildWordLevelGraphics(this.#containerProperties, this.#containers); break;
            case DynamicStylingCreationLevel.Highlight: this._buildHighlightLevelGraphics(this.#containerProperties, this.#containers); break;
            default: throw new Error(`Unrecognized level ${this.level}`);
        }
    }

    _updateAnimation() {
        // build animation
        if (!this.parent) {
            return;
        }
        this.animation.forEach(configurator => {
            configurator.anchorsToRemove = this.parent.animationAnchorsToRemove;
            if (!configurator.enabled) {
                return;
            }
            const animationCurveConfig = configurator.buildConfig();
            this.parent.animationManager.add(animationCurveConfig);
        });
    }

    override load(dataObj, settings: LoadSettings) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        if (this.shape?.graphicType !== dataObj.graphic) {
            this._createShape(dataObj.graphic);
        }
        super.load(dataObj, settings);

        this.autoUpdate = initAutoUpdate;

        return this;
    }
}
